import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import i18n from "../localization/i18n";

const defaultOptions = {
  headers: {
    "Content-Type": "application/json",
    accept: "text/html",
  },
};
export const instance = axios.create(defaultOptions);

// Request Interceptor
instance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token") ?? "{}";
    config.headers!.Authorization = token ? `Bearer ${token}` : "";
    config.headers!.Lang = i18n.language ?? "gb";
    return config;
  },
  (error) => {
    console.error("Request setup error:", error);
    return Promise.reject(error);
  },
);

// Response Interceptor for catching HTTP errors
instance.interceptors.response.use(
  (response) => response,
  (error) => {
    return Promise.reject(error);
  },
);

const responseBody = (response: AxiosResponse) => response.data;

export const requests = {
  get: (url: string, params?: any) =>
    instance
      .get(url, { params })
      .then(responseBody)
      .catch((e) => {
        throw e;
      }),
  post: (url: string, body: {}, customHeaders?: any) =>
    instance
      .post(url, body, customHeaders)
      .then(responseBody)
      .catch((e) => {
        throw e;
      }),
  put: (
    url: string,
    body: {},
    customHeaders?: AxiosRequestConfig<{}> | undefined,
  ) =>
    instance
      .put(url, body, customHeaders)
      .then(responseBody)
      .catch((e) => {
        throw e;
      }),
  patch: (url: string, body: {}, customHeaders?: any) =>
    instance
      .patch(url, body, customHeaders)
      .then(responseBody)
      .catch((e) => {
        throw e;
      }),
  delete: (url: string) =>
    instance
      .delete(url)
      .then(responseBody)
      .catch((e) => {
        throw e;
      }),
};

export const downloadFile = async (
  url: string,
  format?: string,
  fallbackFileName?: string,
  body?: unknown,
) => {
  const token = localStorage.getItem("token") ?? "";

  try {
    const response = await axios.request({
      url,
      method: body !== undefined ? "POST" : "GET",
      responseType: "blob",
      headers: {
        Authorization: token ? `Bearer ${token}` : "",
        ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      },
      ...(body !== undefined ? { data: body } : {}),
    });
    const contentDisposition = response.headers["content-disposition"];
    let fileName = fallbackFileName ?? "file";

    if (contentDisposition) {
      const extractedName =
        getFileNameFromContentDisposition(contentDisposition);
      if (extractedName) {
        fileName = extractedName;
      }
    }

    if (
      format &&
      !fileName.toLowerCase().endsWith(`.${format.toLowerCase()}`)
    ) {
      fileName = `${fileName}.${format}`;
    }

    const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const formDataHeaders = {
  headers: {
    "Content-Type": "multipart/form-data",
  },
};

function getFileNameFromContentDisposition(header: string): string | null {
  const fileNameMatch = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(header);
  if (fileNameMatch != null && fileNameMatch[1]) {
    return fileNameMatch[1].replace(/['"]/g, "");
  }
  return null;
}
